import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  BASEMAP_ADDON_GIS,
  BASEMAP_ESRI_SATELLITE,
} from '@rsos/constants/basemapAddonNames';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { createVectorTileLayer } from '@rsos/maps/providers/esri/layers/createVectorTileLayer';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import { getGISLayerID } from '../utils/getGISLayerID';

const SATELLITE_REFERENCE_LAYER_ID = 'satellite-reference-layer';

const SATELLITE_REFERENCE_LAYER_NAME = 'satellite Reference Layer';

/**
 * Esri basemap transition handling
 * @param {Object} map - The Esri map oject.
 */
const useEsriMapTransition = map => {
  const mapInstance = map?.mapInstance;

  const hasEsriMapAccess = useSelector(state =>
    checkEsriMapAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const hasGISServiceAccess = useSelector(state =>
    checkGISServicesAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const mapAddons = useSelector(state => state.sinatra.ui.mapAddons);

  const isBasemapVisible = useSelector(
    state => state.sinatra.ui.isBasemapVisible,
  );

  // The GIS addon in the basemap menu refers to the Esri GIS cached-basemap service.
  const gisAddon = useSelector(state => state.gisServices.basemapService);

  const hasGISAddon = useMemo(() => {
    return hasGISServiceAccess && !!Object.keys(gisAddon).length;
  }, [hasGISServiceAccess, gisAddon]);

  const selectedCallerID = useSelector(
    state => state?.emergencyCalls?.selectedCallerID,
  );

  const selectedAlertID = useSelector(
    state => state?.incidents?.selectedAlertID,
  );

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const callQueue = useSelector(state =>
    state?.emergencyCalls?.callQueue
      ? callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess)
      : {},
  );

  const hasLocation = !!getMostRecentLoc(callQueue?.[selectedCallerID]);

  const isAllView = useMemo(() => {
    return (!selectedCallerID || !hasLocation) && !selectedAlertID;
  }, [hasLocation, selectedAlertID, selectedCallerID]);

  const findSatelliteReferenceLayer = useCallback(mapInstance => {
    if (mapInstance) {
      return (
        mapInstance.map.findLayerById(SATELLITE_REFERENCE_LAYER_ID) || null
      );
    }

    return null;
  }, []);

  // Create and add Esri satellite imagery reference layer to the layers collection.
  useEffect(() => {
    if (basemap && hasEsriMapAccess && mapInstance && mapProvider === ESRI) {
      mapInstance.view.map.basemap.when(async () => {
        const doesSatelliteReferenceLayerExist =
          !!findSatelliteReferenceLayer(mapInstance);

        if (!doesSatelliteReferenceLayerExist) {
          try {
            const satelliteReferenceLayer = await createVectorTileLayer({
              id: SATELLITE_REFERENCE_LAYER_ID,
              url: 'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/arcgis/imagery/labels',
              visible: basemap === BASEMAP_ESRI_SATELLITE && isBasemapVisible,
              title: SATELLITE_REFERENCE_LAYER_NAME,
            });

            mapInstance.map.add(satelliteReferenceLayer, 0);
          } catch (error) {
            captureExceptionWithScope(error, {
              extra: {
                errorMessage: error?.message,
              },
            });
          }
        }
      });
    }
  }, [
    basemap,
    findSatelliteReferenceLayer,
    hasEsriMapAccess,
    isBasemapVisible,
    mapInstance,
    mapProvider,
  ]);

  // Update the Esri basemap and visibility on the selection in the basemap menu.
  // If the Esri 'none' basemap is selected, the basemap visibility should be false.
  // Otherwise the basemap visibility should be true.
  useEffect(() => {
    if (basemap && hasEsriMapAccess && mapInstance && mapProvider === ESRI) {
      mapInstance.view.map.basemap.when(() => {
        mapInstance.view.map.basemap = basemap;

        // Esri basemap (navigation or satellite imagery) layer.
        const basemapLayer = mapInstance.view.map.basemap.baseLayers.items[0];

        const satelliteReferenceLayer =
          findSatelliteReferenceLayer(mapInstance);

        if (basemapLayer) basemapLayer.visible = isBasemapVisible;

        if (satelliteReferenceLayer)
          satelliteReferenceLayer.visible =
            basemap === BASEMAP_ESRI_SATELLITE && isBasemapVisible;
      });
    }
  }, [
    basemap,
    findSatelliteReferenceLayer,
    hasEsriMapAccess,
    isBasemapVisible,
    mapInstance,
    mapProvider,
  ]);

  // Update the basemap menu addon (Esri GIS cached-basemap layer) visibility based on the selection
  // in the basemap menu.
  useEffect(() => {
    if (
      hasEsriMapAccess &&
      hasGISAddon &&
      mapAddons &&
      mapInstance &&
      mapProvider === ESRI
    ) {
      const gisAddonLayer = mapInstance.map.findLayerById(
        getGISLayerID(gisAddon),
      );

      if (gisAddonLayer)
        gisAddonLayer.visible = mapAddons.includes(BASEMAP_ADDON_GIS);
    }
  }, [
    gisAddon,
    hasEsriMapAccess,
    hasGISAddon,
    mapAddons,
    mapInstance,
    mapProvider,
  ]);

  // When the mapProvider is switched to Esri via the basemap menu,
  // update the map extent to the specific view state where the user was on Google map.
  useEffect(() => {
    if (
      mapInstance &&
      mapProvider === ESRI &&
      window?.__RSOS?.mapViewState &&
      isAllView
    ) {
      const { extent } = window.__RSOS.mapViewState;

      mapInstance.view.extent = extent;

      delete window.__RSOS.mapViewState;
    }
  }, [isAllView, mapInstance, mapProvider]);
};

export default useEsriMapTransition;
